import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaStream } from '@react-icons/all-files/fa/FaStream';
import { FaProjectDiagram } from '@react-icons/all-files/fa/FaProjectDiagram';
import { FaCog } from '@react-icons/all-files/fa/FaCog';
import { FaHome } from '@react-icons/all-files/fa/FaHome';
import Breadcrumb from '../../../../commons/Breadcrumb';
import PageHeader from '../../../../commons/PageHeader';
import PageAlert, { usePageAlert } from '../../../../commons/PageAlert';
import PageForm, { FormField } from '../../../../commons/PageForm';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { createEntity, reset } from '../../../../manager/workflow-instancia.reducer';

const BACK = '/configuracion/workflow/instancias-workflow';

interface InstanciaForm {
  empresa: string;
  workflow: string;
}

const empty: InstanciaForm = { empresa: '', workflow: '' };

export default function InstanciasWorkflowFormPage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const entity = useAppSelector(s => (s as any).workflowInstancia.entity);
  const updating = useAppSelector(s => (s as any).workflowInstancia.updating);
  const updateSuccess = useAppSelector(s => (s as any).workflowInstancia.updateSuccess);
  const errorMessage = useAppSelector(s => (s as any).workflowInstancia.errorMessage);

  const [form, setForm] = useState<InstanciaForm>(empty);
  const [errors, setErrors] = useState<Partial<Record<keyof InstanciaForm, string>>>({});

  const { alert, showToast } = usePageAlert();

  useEffect(() => {
    dispatch(reset());
    setForm(empty);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (updateSuccess) {
      dispatch(reset());
      navigate(BACK, { state: { successMsg: 'Instancia creada correctamente.' } });
    }
  }, [updateSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (errorMessage) showToast('error', errorMessage);
  }, [errorMessage]); // eslint-disable-line react-hooks/exhaustive-deps

  const fields: FormField<InstanciaForm>[] = useMemo(() => [
    {
      name: 'empresa',
      label: 'Empresa',
      type: 'autocomplete-on-demand',
      required: true,
      placeholder: 'Buscar por RFC o razón social…',
      url: 'api/empresas',
      queryParam: 'query',
      valueKey: 'id',
      labelKey: 'razonSocial',
      minChars: 1,
      extraParams: { page: '0', size: '20', sort: 'razonSocial,asc' },
      labelResolver: (_val, raw) => raw ? `${raw.rfc} – ${raw.razonSocial}` : undefined,
      renderOption: (raw, _opt, isSelected) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <span style={{ fontSize: 11, color: '#94a3b8' }}>{raw.rfc}</span>
          <span style={{ fontWeight: isSelected ? 600 : 400 }}>{raw.razonSocial}</span>
          {raw.grupoEmpresarial?.alias && (
            <span style={{ fontSize: 11, color: '#64748b' }}>{raw.grupoEmpresarial.alias}</span>
          )}
        </div>
      ),
    },
    {
      name: 'workflow',
      label: 'Workflow',
      type: 'autocomplete-on-demand',
      required: true,
      placeholder: 'Buscar workflow por nombre…',
      url: 'api/profase-workflows',
      queryParam: 'nombre',
      valueKey: 'id',
      labelKey: 'nombre',
      minChars: 1,
      extraParams: { page: '0', size: '20', sort: 'nombre,asc' },
      labelResolver: (_val, raw) => raw ? raw.nombre : undefined,
      renderOption: (raw, _opt, isSelected) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <span style={{ fontWeight: isSelected ? 600 : 400 }}>{raw.nombre}</span>
          {raw.workflowClase?.nombre && (
            <span style={{ fontSize: 11, color: '#94a3b8' }}>{raw.workflowClase.nombre}</span>
          )}
        </div>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [entity]);

  const handleFieldChange = (name: keyof InstanciaForm, value: string) => {
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Partial<Record<keyof InstanciaForm, string>> = {};
    if (!form.empresa) errs.empresa = 'La empresa es requerida';
    if (!form.workflow) errs.workflow = 'El workflow es requerido';
    if (Object.keys(errs).length) { setErrors(errs); return; }

    dispatch(createEntity({
      workflowEmpresa: {
        empresa: { id: Number(form.empresa) },
        workflow: { id: Number(form.workflow) },
      }
    }));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <Breadcrumb items={[
        { label: 'Inicio', to: '/', icon: <FaHome /> },
        { label: 'Configuración', to: '/configuracion', icon: <FaCog /> },
        { label: 'Workflow', to: '/configuracion/workflow', icon: <FaProjectDiagram /> },
        { label: 'Instancias de Workflow', to: BACK, icon: <FaStream /> },
        { label: 'Nueva Instancia' },
      ]} />

      <PageHeader
        icon={<FaStream />}
        iconColor="#f59e0b"
        title="Nueva Instancia de Workflow"
        subtitle="Genera una nueva instancia de workflow para una empresa"
      />

      {alert && <PageAlert alert={alert} />}

      <PageForm
        fields={fields}
        values={form}
        errors={errors}
        isNew={true}
        saving={updating}
        onFieldChange={handleFieldChange}
        onSubmit={handleSubmit}
        onCancel={() => navigate(BACK)}
        accentColor="#f59e0b"
      />
    </div>
  );
}
